import {
  AddCircle,
  ArrowDownward,
  ArrowDropDown,
  ArrowUpward,
  Delete,
  Settings,
  Sort,
  Star,
  UnfoldLess,
  UnfoldMore,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Dispatch, Fragment, MouseEvent, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import theme, { EllipsisSX, TransparentMenuItemSX, LinkSX } from "theme";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { hideCellId, selectHiddenCellIds, showCellId } from "redux/slices/session";
import { FormFieldContext, GroupedValueGroup } from "contexts/FormFieldContext";
import {
  cellValueFromGQL,
  diffInDays,
  displayDate,
  formatRelativeTime,
  FormFieldValueGroupUI,
  gqlCellValueFromValue,
  INTERNAL_USER_NAME,
  isEditViewState,
  parseDate,
  returnToViewState,
  RowViewState,
  UNGROUPED_CELL_ID,
} from "Backend";
import SimpleMenu from "../SimpleMenu";
import { Cell, CellType, FieldValueGroupSelectionStatus, FormField, ValueGroupSummaryData } from "generated/graphql";
import DateRange from "components/DateRange";
import { ValueGroupStar } from "./ValueGroupStar";
import { P } from "theme/typography";
import { FieldValueGroupReferences } from "./FieldValueGroupReferenceCard";
import SourceTypeIcon from "./SourceTypeIcon";
import FormFieldValueGroupEditor from "./FormFieldValueGroupEditor";
import ValueGroupNotes from "./ValueGroupNotes";
import { distinctReferencesInGroup } from "./form-field-helpers";
import displayCellValueObject, { displayCellValueString } from "./displayCellValue";
import displayCellValue from "./displayCellValue";
import { CaseFormContext } from "contexts/CaseFormContext";
import { selectCaseInfo } from "redux/slices/case";
import DescriptionTooltip from "./DescriptionTooltip";
import { selectUserById } from "redux/slices/client";
import { fullName } from "helpers/name-helpers";
import { Edit } from "components/Icons";
import CheckAll from "routes/lenses/lens/files/CheckAll";
import { empty, pluralize, stripNull } from "helpers/coll";
import { usePopover } from "helpers/hooks/usePopover";
import { cn } from "components/lib/utils";
import { sortValueGroups } from "./sortValueGroups";
import { ageAtDateFromCellValue } from "helpers/ageAtDateFromCellValue";
import { SortableTH } from "components/SortableTH";
import { TagDisplay } from "components/BetterResponseTable/TagDisplay";
import { EntryContext } from "components/tags/types";
import { EntryTableExpander } from "./EntryTableExpander";

function compareValueGroupUI(a: FormFieldValueGroupUI, b: FormFieldValueGroupUI): boolean {
  return a.id === b.id;
}

function EntryTitle({
  editMode,
  group,
  formField,
}: {
  editMode: boolean;
  group: FormFieldValueGroupUI;
  formField: FormField;
}) {
  const foundUser = useAppSelector((state) =>
    group.updatedBy !== null && group.updatedBy !== undefined ? selectUserById(state, group.updatedBy) : undefined
  );
  const title = (
    <P variant="subtitle3" sx={{ mr: 1 }}>
      {editMode ? "Edit Entry" : "Entry"} {group.isNew ? "" : formField.repeating ? `#${group.responseNumber}` : ""}
      {group.touched && " *"}
    </P>
  );
  const differenceInDays = diffInDays(stripNull(group.createdAt), stripNull(group.updatedAt));
  const editedBy =
    foundUser !== undefined || group.updatedBy !== undefined
      ? `Last updated by ${foundUser !== undefined ? fullName(foundUser) : INTERNAL_USER_NAME}${group.updatedAt ? ` ${formatRelativeTime(group.updatedAt)}.` : "."}`
      : "";
  const createdAt =
    differenceInDays !== undefined && differenceInDays > 0
      ? `\nOriginally created on ${displayDate(parseDate(group.createdAt))}.`
      : "";
  const tooltip = editedBy + createdAt;
  return tooltip !== "" ? <Tooltip title={tooltip}>{title}</Tooltip> : title;
}

function EntryRow({
  group,
  groupPositionalIndex,
  visibleCellIds,
  onReferenceClick,
  selectedValueGroups,
  onSelect,
  colSpan,
  readonly,
  tableLayout,
}: {
  group: FormFieldValueGroupUI;
  groupPositionalIndex: number;
  visibleCellIds: string[];
  selectedValueGroups: FormFieldValueGroupUI[];
  onSelect: (valueGroup: FormFieldValueGroupUI) => void;
  onReferenceClick: (context: EntryContext) => void;
  colSpan?: number;
  readonly?: boolean;
  tableLayout?: "fixed" | "auto";
}) {
  const ffContext = useContext(FormFieldContext);
  const cfContext = useContext(CaseFormContext);
  const caseInfo = useAppSelector(selectCaseInfo);
  const canSwapUp = !readonly && !ffContext.isValueGroupFirst(group.id) && !ffContext.fieldUIState?.filterByStarred;
  const canSwapDown = !readonly && !ffContext.isValueGroupLast(group.id) && !ffContext.fieldUIState?.filterByStarred;
  const deleteValueGroupPopover = usePopover();
  const isRowSelected = !!selectedValueGroups.find((vg) => vg.id === group.id);
  const disableSwap = ffContext.groupByCellIdHandle[0] !== UNGROUPED_CELL_ID || cfContext.mutationInProgressHandle[0];
  const dob = parseDate(caseInfo?.dateOfBirth);

  // Must wait for the popover to be set before attempting to remove the value group
  useEffect(() => {
    if (deleteValueGroupPopover.isOpen) {
      ffContext.removeValueGroup(
        group.id,
        () => {
          if (isRowSelected) {
            onSelect(group);
          }
        },
        deleteValueGroupPopover
      );
    }
  }, [deleteValueGroupPopover.isOpen]);

  const cancelEdit = () => {
    if (group.isNew) {
      ffContext.removeValueGroup(group.id, () => {});
    } else {
      cfContext.resetFieldLocal(ffContext.formField.id);
      ffContext.setValueGroupViewState(group.id, returnToViewState(group.viewState));
    }
  };

  const save = async () => {
    // Force errors to display in case the value is invalid
    ffContext.setValueGroupErrors(group.id);

    await ffContext.saveValueGroup(group.id, returnToViewState(group.viewState));
  };

  const SelectionSX = {
    fontWeight: group.selectionStatus === FieldValueGroupSelectionStatus.Selected ? "bold" : "normal",
  };

  const distinctRefs = distinctReferencesInGroup(group);
  const TableRowId = `field-${ffContext.formField.id}-response-${group.id}`;

  return group.viewState === RowViewState.ReadCollapsed ? (
    <TableRow
      id={TableRowId}
      className="cursor-pointer"
      data-pendo={`profile-response-row-${groupPositionalIndex}`}
      onClick={(e: MouseEvent<HTMLTableRowElement>) => {
        const tagName = (e.target as HTMLElement).tagName;
        // Tag name is TD or SPAN (date cell) if it is cell data
        if (tagName === "TD" || tagName === "SPAN") {
          ffContext.setValueGroupViewState(group.id, RowViewState.ReadExpanded);
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        ffContext.setValueGroupViewState(group.id, RowViewState.EditCollapsed);
      }}
      hover>
      <TableCell className="flex items-center gap-1">
        {!readonly && (
          <Checkbox
            className="px-0"
            checked={isRowSelected}
            onChange={() => onSelect(group)}
            size="small"
            color="secondary"
          />
        )}
        <ValueGroupStar group={group} readonly={readonly} />
        {group.availableTags?.length > 0 && (
          <TagDisplay.Dense
            valueGroupSerialId={group.serialId}
            appliedTags={group.tags}
            availableTags={group.availableTags}
          />
        )}
      </TableCell>

      {group.values.map((v, cellIndex) => {
        const vgvh = ffContext.valueGroupValuesHandleForGroup(group.id);
        if (vgvh === undefined) {
          return <></>;
        }
        const [state] = vgvh;
        const cellValue = state.find((s) => s.cellId === v.cellId);
        const possibleStringValue = cellValue !== undefined ? cellValueFromGQL(cellValue.value)?.toString() : "";
        const valueDisplay = cellValue !== undefined ? displayCellValueObject(cellValue.value, caseInfo) : "";
        const valueDisplayString = displayCellValueString(cellValue?.value);
        const cell = ffContext.formField.cells.find((c) => c.id === v.cellId);
        const canDisplay = visibleCellIds.indexOf(cell?.id.toString() ?? "") > -1;
        const ageAtDate = dob !== undefined ? ageAtDateFromCellValue(dob, cellValue?.value) : undefined;
        return canDisplay && ageAtDate !== undefined ? (
          <TableCell
            key={cellIndex}
            title={`Age at ${valueDisplayString}: ${ageAtDate}`}
            sx={{
              ...EllipsisSX,
              ...SelectionSX,
              backgroundColor: readonly && cellIndex % 2 === 1 ? theme.palette.grey[50] : undefined,
              px: tableLayout === "auto" ? 0.5 : 0,
              py: readonly ? 0.5 : 0,
            }}>
            {valueDisplayString}
          </TableCell>
        ) : canDisplay ? (
          <TableCell
            key={cellIndex}
            sx={{
              ...EllipsisSX,
              ...SelectionSX,
              backgroundColor: readonly && cellIndex % 2 === 1 ? theme.palette.grey[50] : undefined,
              px: tableLayout === "auto" ? 0.5 : 0,
              py: readonly ? 0.5 : 0,
            }}
            title={possibleStringValue ?? ""}>
            {valueDisplay}
          </TableCell>
        ) : (
          <Fragment key={cellIndex}></Fragment>
        );
      })}
      <TableCell>
        <div className="flex items-center">
          {distinctRefs !== undefined && distinctRefs > 0 && (
            <p
              className="pattern-link text-peri-900 min-w-fit whitespace-nowrap"
              onClick={(_: MouseEvent<HTMLParagraphElement>) => {
                onReferenceClick({ valueGroupId: group.id, referenceId: group.references[0]!.id });
              }}>
              {distinctRefs ?? ""} Ref
            </p>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <SourceTypeIcon
            className="h-4 w-4 text-gray-400"
            valueGroupSource={stripNull(group.source)}
            formField={ffContext.formField}
            valueGroup={group}
          />
        </Box>
      </TableCell>
    </TableRow>
  ) : group.viewState === RowViewState.ReadExpanded ? (
    <TableRow id={TableRowId}>
      <TableCell colSpan={colSpan} className="border border-gray-100 shadow rounded-md">
        <div className="grid grid-cols-4 gap-2 bg-gray-50 p-2">
          <div className="col-span-3">
            <div className="flex items-center h-6 mb-2">
              <div className="flex items-center grow gap-2">
                {!readonly && (
                  <Checkbox
                    checked={isRowSelected}
                    onChange={() => onSelect(group)}
                    size="small"
                    color="secondary"
                    sx={{
                      p: 0,
                    }}
                  />
                )}
                <ValueGroupStar group={group} readonly={readonly} />
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => ffContext.setValueGroupViewState(group.id, RowViewState.ReadCollapsed)}>
                  <EntryTitle group={group} editMode={false} formField={ffContext.formField} />
                  <IconButton size="small" color="default" sx={{ px: 0 }}>
                    <ArrowDropDown />
                  </IconButton>
                </div>
                {group.source && (
                  <SourceTypeIcon
                    className="h-4 w-4"
                    valueGroupSource={group.source}
                    formField={ffContext.formField}
                    valueGroup={group}
                  />
                )}
                <div className="grow flex justify-end">
                  {canSwapUp && (
                    <Tooltip
                      title={disableSwap ? "Cannot change order when grouped" : `Move to rank #${groupPositionalIndex}`}
                      arrow>
                      <span>
                        <IconButton
                          disabled={disableSwap}
                          color="default"
                          id={"pfield_swap_up_" + ffContext.formField.id + "_" + group.id}
                          size="small"
                          onClick={(evt) => ffContext.swapValueGroups(evt, group.id, "up")}>
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {canSwapDown && (
                    <Tooltip
                      title={
                        disableSwap
                          ? "Cannot change order when grouped"
                          : `Move down to rank #${groupPositionalIndex + 2}`
                      }
                      arrow>
                      <span>
                        <IconButton
                          disabled={disableSwap}
                          color="default"
                          id={"pfield_swap_down_" + ffContext.formField.id + "_" + group.id}
                          size="small"
                          onClick={(evt) => ffContext.swapValueGroups(evt, group.id, "down")}>
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {!readonly && (
                    <Tooltip title={isRowSelected ? "Cannot edit selected row" : "Edit Entry"}>
                      <span>
                        <IconButton
                          size="small"
                          color="default"
                          onClick={() => ffContext.setValueGroupViewState(group.id, RowViewState.EditExpanded)}
                          disabled={isRowSelected}>
                          <Edit />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
            {group.availableTags.length > 0 && (
              <TagDisplay
                className="mb-1"
                availableTags={group.availableTags}
                appliedTags={group.tags}
                valueGroupSerialId={group.serialId}
              />
            )}
            <div className="flex">
              <Card variant="outlined" sx={{ flexGrow: 1 }}>
                <CardContent
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
                    gap: "16px 16px",
                  }}>
                  {group.values.map((v) => {
                    const cell = ffContext.cellByIdMap.get(v.cellId);
                    const valueDisplay = displayCellValue(v.value, caseInfo);

                    if (cell === undefined) {
                      return <></>;
                    } else {
                      return (
                        <Fragment key={cell.id}>
                          <div className="flex items-start gap-2">
                            <p className="font-semibold text-xs break-words" title={cell.name}>
                              {cell.name}:
                            </p>
                            <DescriptionTooltip description={cell.description} />
                          </div>
                          <p className="text-xs break-words">{valueDisplay}</p>
                        </Fragment>
                      );
                    }
                  })}
                </CardContent>
              </Card>
            </div>
            {group.notes && (
              <Card variant="outlined" sx={{ mt: 1, maxHeight: 103, overflow: "auto" }}>
                <CardContent>
                  <P variant="body3" sx={{ mb: 1 }}>
                    Notes
                  </P>
                  <P variant="body4" sx={{ display: "block", whiteSpace: "pre-line" }}>
                    {group.notes}
                  </P>
                </CardContent>
              </Card>
            )}
          </div>
          <div className="flex flex-col">
            {distinctRefs ? (
              <P
                variant="subtitle3"
                sx={{
                  height: 26,
                  mb: 1,
                  ...LinkSX,
                }}
                onClick={() => onReferenceClick({ valueGroupId: group.id, referenceId: group.references[0]!.id })}>
                {`${distinctRefs} ${pluralize(distinctRefs, "Reference")}`}
              </P>
            ) : (
              <P variant="subtitle3" sx={{ height: 26, mb: 1 }}>
                References
              </P>
            )}
            <FieldValueGroupReferences onClick={onReferenceClick} valueGroup={group} />
          </div>
        </div>
      </TableCell>
    </TableRow>
  ) : (
    <TableRow id={TableRowId} className="bg-gray-50 border-2 border-gray-50">
      <TableCell colSpan={colSpan} className="border border-gray-100 shadow rounded-md">
        <div className="grid grid-cols-4 gap-2 p-2">
          <div className="col-span-3">
            <div className="flex items-center mb-2 h-6 gap-2">
              <P variant="subtitle3">
                Edit Entry{" "}
                {ffContext.formField.repeating
                  ? `#${group.isNew ? groupPositionalIndex + 1 : group.responseNumber}`
                  : ""}{" "}
                {group.touched && "*"}
              </P>
              <Button
                id={"pfield_delete_response_" + ffContext.formField.id + "_" + group.id}
                size="small"
                color="default"
                disabled={cfContext.mutationInProgressHandle[0]}
                endIcon={<Delete fontSize="small" />}
                onClick={(evt) => {
                  deleteValueGroupPopover.onPopoverOpen(evt.currentTarget);
                  evt.stopPropagation();
                }}>
                Delete
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                color="default"
                onClick={cancelEdit}
                disabled={cfContext.mutationInProgressHandle[0]}>
                Cancel
              </Button>
              <Button
                size="small"
                color="primary"
                onClick={save}
                disabled={cfContext.mutationInProgressHandle[0]}
                variant="contained">
                Save
              </Button>
            </div>
            {group.availableTags.length > 0 && (
              <TagDisplay
                className="mb-1"
                availableTags={group.availableTags}
                appliedTags={group.tags}
                valueGroupSerialId={group.serialId}
              />
            )}
            <Card variant="outlined" className="max-h-80 overflow-auto">
              <CardContent className="grid grid-cols-3 gap-2">
                <FormFieldValueGroupEditor
                  cells={ffContext.formField.cells}
                  groupId={group.id}
                  values={group.values}
                  caseInfo={caseInfo}
                />
              </CardContent>
            </Card>
            <Card variant="outlined" className="mt-2">
              <CardContent>
                <P variant="body3" sx={{ mb: 1 }}>
                  Notes
                </P>
                <ValueGroupNotes group={group} />
              </CardContent>
            </Card>
          </div>
          <div className="flex flex-col">
            <P variant="subtitle3" sx={{ marginBottom: 1, display: "block", height: 26 }}>
              References
            </P>
            <FieldValueGroupReferences onClick={onReferenceClick} valueGroup={group} canDelete />
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

function EntryTableHeader({
  summaryData,
  visibleCellIds,
  hideCellSelector,
  selectedValueGroups,
  setSelectedValueGroups,
  expandActionShouldExpand,
  groupName,
  count,
  expanded,
  setExpanded,
  showExpander = true,
}: {
  visibleCellIds: string[];
  summaryData?: ValueGroupSummaryData;
  hideCellSelector?: boolean;
  selectedValueGroups: FormFieldValueGroupUI[];
  setSelectedValueGroups: (valueGroups: FormFieldValueGroupUI[]) => void;
  expandActionShouldExpand: boolean;
  groupName?: string;
  count: number;
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  showExpander?: boolean;
}) {
  const dispatch = useAppDispatch();
  const ffContext = useContext(FormFieldContext);
  const numberColumnsHidden = ffContext.formField.cells.length - visibleCellIds.length;
  const starAction: FieldValueGroupSelectionStatus = useMemo(
    () =>
      selectedValueGroups.every((vg) => vg.selectionStatus === FieldValueGroupSelectionStatus.Selected)
        ? FieldValueGroupSelectionStatus.Default
        : FieldValueGroupSelectionStatus.Selected,
    [selectedValueGroups]
  );
  const deleteValueGroupsPopover = usePopover();
  const showDateRange = ffContext.state.yesNoValue === true && ffContext.groupByCellIdHandle[0] === UNGROUPED_CELL_ID;

  const handleExpandAll = () => {
    const filteredValueGroups = selectedValueGroups.filter((vg) => !isEditViewState(vg.viewState));
    const valueGroupIds = filteredValueGroups.map((vg) => vg.id);
    ffContext.setValueGroupViewStateBulk(
      valueGroupIds,
      expandActionShouldExpand ? RowViewState.ReadExpanded : RowViewState.ReadCollapsed
    );
    setSelectedValueGroups([]);
  };

  // Must wait for the popover to be set before attempting to remove the value groups
  useEffect(() => {
    if (deleteValueGroupsPopover.isOpen) {
      ffContext.removeValueGroups(
        selectedValueGroups.map((vg) => vg.id),
        () => {
          setSelectedValueGroups([]);
        },
        deleteValueGroupsPopover,
        groupName
      );
    }
  }, [deleteValueGroupsPopover.isOpen]);

  return (
    <div className="flex items-center gap-2 min-h-6">
      {showExpander && (
        <EntryTableExpander
          text={
            <span className="font-semibold">
              {count} {pluralize(count, "Entry", "Entries")}
            </span>
          }
          expanded={expanded}
          setExpanded={setExpanded}
        />
      )}
      {selectedValueGroups.length > 0 && (
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-gray-500">{selectedValueGroups.length} Selected</p>
          <Tooltip title={expandActionShouldExpand ? "Expand Selected" : "Collapse Selected"}>
            <IconButton size="small" className="p-0 opacity-60" onClick={handleExpandAll}>
              {expandActionShouldExpand ? <UnfoldMore /> : <UnfoldLess />}
            </IconButton>
          </Tooltip>
          <Star
            className={cn(
              "h-4 w-4 cursor-pointer hover:text-gold hover:opacity-100",
              starAction === FieldValueGroupSelectionStatus.Default ? "opacity-100 text-gold" : "opacity-40"
            )}
            onClick={(e) => {
              e.stopPropagation();
              ffContext.selectValueGroups(
                selectedValueGroups.map((vg) => vg.id),
                starAction
              );
              setSelectedValueGroups([]);
            }}
          />
          <Delete
            className="h-4 w-4 opacity-40 cursor-pointer hover:opacity-100"
            onClick={(evt) => deleteValueGroupsPopover.onPopoverOpen(evt.currentTarget)}
          />
        </div>
      )}
      {showDateRange && (
        <DateRange start={summaryData?.minDate} end={summaryData?.maxDate} style={{ marginRight: 8 }} />
      )}
      <div className="grow" />
      {!hideCellSelector && (
        <Select
          data-pendo={`column-selector-${ffContext.formField.id}`}
          size="small"
          labelId="column-hide-label"
          multiple
          IconComponent={Settings}
          value={visibleCellIds}
          renderValue={() => `${numberColumnsHidden} ${pluralize(numberColumnsHidden, "Column")} Hidden`}
          onChange={(_evt: SelectChangeEvent<string[]>, child: any) => {
            if (child.props?.value) {
              const cellId = parseInt(child.props.value, 10);
              if (child.props.children[0].props.checked) {
                dispatch(hideCellId({ cellId })).catch((error) =>
                  console.error("EntryTable; Error hiding the cell", error)
                );
              } else {
                dispatch(showCellId({ cellId })).catch((error) =>
                  console.error("EntryTable; Error showing the cell", error)
                );
              }
            }
          }}>
          {ffContext.formField.cells.map((cell) => (
            <MenuItem key={cell.id} value={`${cell.id}`} sx={TransparentMenuItemSX}>
              <Checkbox checked={visibleCellIds.indexOf(`${cell.id}`) > -1} />
              <ListItemText primary={cell.name} />
            </MenuItem>
          ))}
        </Select>
      )}
    </div>
  );
}

export function EntryTable(props: {
  hideCellSelector?: boolean;
  valueGroups: FormFieldValueGroupUI[];
  groupByCellToHide?: number;
  group?: GroupedValueGroup;
  groupName?: string;
  readonly?: boolean;
  compactMode?: boolean;
  tableLayout?: "fixed" | "auto";
  activeSectionId?: number;
  onReferenceClick: (context: EntryContext) => void;
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  showExpander?: boolean;
}) {
  const ffContext = useContext(FormFieldContext);
  const groupSort = ffContext.fieldUIState?.groupSorts[props.groupName ?? ""];
  const localSortDir = groupSort?.sortDir;
  const tableLayout = props.tableLayout ?? "fixed";
  const [localValueGroups, setLocalValueGroups] = useState(
    groupSort
      ? (sortValueGroups(props.valueGroups, groupSort.sortDir, groupSort.cell) as FormFieldValueGroupUI[])
      : props.valueGroups
  );
  const hiddenCellIds = useAppSelector(selectHiddenCellIds);
  const visibleCellIds: string[] = useMemo(
    () =>
      ffContext.formField.cells
        .filter((c) => hiddenCellIds.indexOf(c.id) === -1 && c.id !== props.groupByCellToHide)
        .map((c) => c.id.toString()),
    [hiddenCellIds]
  );
  const [selectedValueGroups, setSelectedValueGroups] = useState<FormFieldValueGroupUI[]>([]);
  const colSpan = 4 + visibleCellIds.length;
  const visibleCells = useMemo(
    () => ffContext.formField.cells.filter((c) => visibleCellIds.indexOf(`${c.id}`) > -1),
    [ffContext.formField.cells, visibleCellIds, props.groupByCellToHide]
  );

  useEffect(() => {
    setLocalValueGroups(
      groupSort
        ? (sortValueGroups(props.valueGroups, groupSort.sortDir, groupSort.cell) as FormFieldValueGroupUI[])
        : props.valueGroups
    );
  }, [props.valueGroups, groupSort]);
  const hasAnExpandedSelectedRow = selectedValueGroups.some((vg) => vg.viewState === RowViewState.ReadExpanded);

  function onValueGroupSelected(group: FormFieldValueGroupUI) {
    if (selectedValueGroups.find((vg) => vg.id === group.id)) {
      setSelectedValueGroups(selectedValueGroups.filter((vg) => vg.id !== group.id));
    } else {
      setSelectedValueGroups([...selectedValueGroups, group]);
    }
  }

  const disableServerSort = ffContext.groupByCellIdHandle[0] !== UNGROUPED_CELL_ID;

  const addValueGroup = () => {
    if (ffContext.groupByCell === undefined) {
      ffContext.addValueGroup();
    } else {
      ffContext.addValueGroup({
        cellId: ffContext.groupByCell.id,
        value: gqlCellValueFromValue(ffContext.groupByCell.type, props.group?.originalValue || props.groupName),
      });
    }
  };

  const restoreServerSort = (cellValue: string) => {
    ffContext.setGroupSort(cellValue);
    setLocalValueGroups(props.valueGroups);
  };

  const sortLocal = (cellValue: string, cell: Cell, sortDir: "asc" | "desc") => {
    ffContext.setGroupSort(cellValue, cell, sortDir);
    setLocalValueGroups(sortValueGroups(localValueGroups, sortDir, cell) as FormFieldValueGroupUI[]);
  };

  const hasAnyTag = empty(props.valueGroups) ? false : props.valueGroups[0]!.availableTags.length > 0;

  return (
    <div className={cn(["flex flex-col px-2"], props.compactMode && ["max-h-[500px]"])}>
      <EntryTableHeader
        visibleCellIds={visibleCellIds}
        summaryData={ffContext.state.summaryData}
        hideCellSelector={props.hideCellSelector}
        selectedValueGroups={selectedValueGroups}
        setSelectedValueGroups={setSelectedValueGroups}
        expandActionShouldExpand={!hasAnExpandedSelectedRow}
        groupName={props.groupName}
        count={localValueGroups.length}
        expanded={props.expanded}
        setExpanded={props.setExpanded}
        showExpander={props.showExpander}
      />
      {props.expanded && (
        <div className={cn(["flex flex-col w-full"], tableLayout === "auto" ? ["overflow-x-auto"] : [])}>
          <Table stickyHeader size="small" style={{ tableLayout }} padding="none">
            <TableHead style={{ minHeight: "100px" }}>
              <TableRow>
                {/* table-layout is fixed so we need to do pixels here */}
                <TableCell style={{ width: hasAnyTag ? 105 : 40 }}>
                  {!props.readonly && (
                    <CheckAll<FormFieldValueGroupUI>
                      checkboxProps={{
                        className: "pl-0",
                        disableRipple: true,
                      }}
                      selectedItems={selectedValueGroups}
                      items={localValueGroups}
                      setItems={setSelectedValueGroups}
                      compareFn={compareValueGroupUI}
                    />
                  )}
                </TableCell>
                {visibleCells.map((cell) => {
                  return !props.readonly &&
                    localValueGroups.length > 1 &&
                    disableServerSort &&
                    cell.type !== CellType.MultiSelect ? (
                    <SortableTH
                      key={cell.id}
                      className={[[tableLayout === "auto" ? "px-1 odd:bg-gray-50" : ""]]}
                      sortDir={localSortDir}
                      amISorted={groupSort?.cell.id === cell.id}
                      onSortAsc={() => sortLocal(props.groupName ?? "", cell, "asc")}
                      onSortDesc={() => sortLocal(props.groupName ?? "", cell, "desc")}
                      onSortClear={() => restoreServerSort(props.groupName ?? "")}>
                      <div className="truncate" title={cell.name}>
                        {cell.name}
                      </div>
                      {!cell.optional && " *"}
                    </SortableTH>
                  ) : (
                    <TableCell key={cell.id} className={cn([tableLayout === "auto" ? "px-1 odd:bg-gray-50" : ""])}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box title={cell.name} sx={{ ...EllipsisSX }}>
                          {cell.name}
                        </Box>
                        {!cell.optional && " *"}
                        {!props.readonly && localValueGroups.length > 1 && !disableServerSort && (
                          <Box sx={{ flexShrink: 0 }}>
                            <SimpleMenu
                              iconTooltip="Sort"
                              iconTooltipProps={{ placement: "top" }}
                              customIcon={<Sort style={{ transform: "scaleY(-1)", fontSize: "14px" }} />}
                              items={[
                                {
                                  name: "Ascending",
                                  icon: <Sort style={{ transform: "scaleY(-1)" }} />,
                                  action: () => ffContext.sortResponses(cell.id, "ascending"),
                                  type: "action",
                                },
                                {
                                  name: "Descending",
                                  icon: <Sort />,
                                  action: () => ffContext.sortResponses(cell.id, "descending"),
                                  type: "action",
                                },
                              ]}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
                <TableCell sx={{ width: 20 }} />
                <TableCell data-pendo="response-table-references" sx={{ width: 40 }}>
                  <Box sx={{ display: "flex", justifyContent: "center", minWidth: 35 }}>Ref</Box>
                </TableCell>
                <TableCell sx={{ width: 0 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {localValueGroups.map((group, idx) => (
                <EntryRow
                  readonly={props.readonly}
                  groupPositionalIndex={idx}
                  key={group.id}
                  group={group}
                  visibleCellIds={visibleCellIds}
                  onReferenceClick={props.onReferenceClick}
                  selectedValueGroups={selectedValueGroups}
                  onSelect={onValueGroupSelected}
                  colSpan={colSpan}
                  tableLayout={tableLayout}
                />
              ))}
              {!props.readonly && ffContext.canAddValueGroup && (
                <TableRow>
                  <TableCell colSpan={colSpan}>
                    <Button
                      color="default"
                      id={"pfield_add_response_" + ffContext.formField.id}
                      onClick={addValueGroup}
                      startIcon={<AddCircle />}>
                      Add Entry
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
